import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { ApiError } from "../../lib/api";
import { meetingTitle, toneOf, TONE_LABELS } from "../../lib/meetings-view";
import { queryKeys } from "../../lib/queryKeys";
import { salesApi } from "../../lib/sales-api";
import type { SalesMeeting, SalesMeetingDetail } from "../../lib/sales-types";
import type { User } from "../../types";
import { Avatar } from "../ui/Avatar";
import { cn } from "../ui/cn";
import { canChange } from "./MeetingsCalendar";
import { Fact, duration, type PanelFocus } from "./MeetingPanel";
import { longDay, timeRange } from "./MeetingRow";

/**
 * A meeting at a glance, in a card beside the block that was clicked -- Google
 * Calendar's event card.
 *
 * The calendar is for seeing the week, and the pane that opened on every click
 * took a third of it away to answer what was usually "who is that with, and
 * where". The card answers that over the grid and leaves it where it was:
 * when, where (the Meet button or the address for the satnav), the company,
 * and everybody going. The pencil, the envelope and the notes line open the
 * full pane on the edit, the follow-up or the notes, so anything longer than
 * a glance is still one press away and is done where there is room for it.
 *
 * Desktop only. On a phone there is no grid beside the block to keep, and the
 * pane is the whole screen already -- which is what Google does there too.
 *
 * Drawn from the diary row straight away and filled in from the meeting's own
 * read, which is the pane's query: the guest list is only on the detail, and
 * opening the pane after the card finds it already loaded.
 */

const WIDTH = 384;
const GAP = 8;
/** People listed before the rest fold into "and N more". */
const SHOWN = 6;

export function MeetingPeek({
  meeting: listed,
  anchor,
  user,
  onClose,
  onOpen,
}: {
  meeting: SalesMeeting;
  /** The block that was clicked, so the card sits beside it. */
  anchor: DOMRect;
  user: User;
  onClose: () => void;
  /** Open the full pane, optionally on one part of it. */
  onOpen: (focus?: PanelFocus) => void;
}) {
  const client = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(anchor.top);

  const detail = useQuery({
    queryKey: queryKeys.salesMeeting(listed.id),
    queryFn: () => salesApi.meeting(listed.id),
    refetchOnWindowFocus: false,
  });

  const act = useMutation({
    mutationFn: (action: () => Promise<SalesMeetingDetail>) => action(),
    onSuccess: (next) => {
      client.setQueryData(queryKeys.salesMeeting(listed.id), next);
      void client.invalidateQueries({ queryKey: queryKeys.salesRoot });
    },
  });

  /* The detail once it is here, the diary's row until then -- and after a
     cancel, the detail is the one that knows. */
  const row: SalesMeeting = detail.data ?? listed;
  const guests = detail.data?.guests ?? null;

  /* Beside the block, the side with room, Google's way; then down from the
     block's top, nudged up when it would run off the bottom. */
  const left =
    anchor.right + GAP + WIDTH <= window.innerWidth - GAP
      ? anchor.right + GAP
      : anchor.left - GAP - WIDTH >= GAP
        ? anchor.left - GAP - WIDTH
        : Math.max(GAP, (window.innerWidth - WIDTH) / 2);

  useLayoutEffect(() => {
    const height = ref.current?.offsetHeight ?? 0;
    setTop(Math.max(GAP, Math.min(anchor.top, window.innerHeight - GAP - height)));
  }, [anchor.top, guests?.length, row.status, act.isError]);

  /* Outside press and Escape close it. Capturing, so a press on another block
     closes this card before that block opens its own. */
  useEffect(() => {
    const onDown = (event: PointerEvent): void => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape" || document.querySelector("[role=dialog]:not([data-peek])")) return;
      event.stopPropagation();
      onClose();
    };
    window.addEventListener("pointerdown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [onClose]);

  const tone = toneOf(row);
  const open = row.status === "scheduled";
  /* The server's rule: somebody going, a manager, or nobody on it at all.
     `canChange` says the same of a scheduled meeting; a cancelled one can
     only be put back. */
  const mine =
    user.role === "admin" ||
    row.attendees.length === 0 ||
    row.attendees.some((person) => person.userId === user.id);
  const editable = canChange(row, user);
  const past = new Date(row.endAt ?? row.startAt) < new Date();
  const minutes = row.endAt
    ? Math.round((new Date(row.endAt).getTime() - new Date(row.startAt).getTime()) / 60_000)
    : null;
  const going = row.attendees.length + (guests?.length ?? row.externalGuestCount);

  return (
    <div
      ref={ref}
      role="dialog"
      data-peek
      aria-label={meetingTitle(row)}
      style={{ left, top, width: WIDTH, maxHeight: window.innerHeight - 2 * GAP }}
      className="fixed z-50 flex flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-2xl"
    >
      {/* ── Google's row of icons ─────────────────────────────────────── */}
      <div className="flex shrink-0 items-center justify-end gap-0.5 px-2 pt-2">
        {editable ? (
          <IconButton label="Edit" onClick={() => onOpen("reschedule")}>
            <path d="M4 20h4L19 9l-4-4L4 16v4z" />
            <path d="m14 6 4 4" />
          </IconButton>
        ) : null}
        {editable ? (
          <IconButton
            label="Cancel the meeting"
            disabled={act.isPending}
            onClick={() => act.mutate(() => salesApi.cancelMeeting(row.id))}
          >
            <path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
          </IconButton>
        ) : null}
        <IconButton label="Email a follow-up" onClick={() => onOpen("follow-up")}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m3 7 9 6 9-6" />
        </IconButton>
        <IconButton label="Open" onClick={() => onOpen()}>
          <path d="M14 4h6v6M20 4l-8 8M10 4H4v16h16v-6" />
        </IconButton>
        <IconButton label="Close (Esc)" onClick={onClose}>
          <path d="M18 6 6 18M6 6l12 12" />
        </IconButton>
      </div>

      <div className="min-h-0 space-y-3 overflow-y-auto px-5 pb-5 pt-1">
        {/* ── What and when ────────────────────────────────────────────── */}
        <div className="flex gap-3">
          <span
            aria-hidden
            className={cn("mt-1.5 size-3.5 shrink-0 rounded", SWATCH[tone])}
          />
          <div className="min-w-0 flex-1">
            <h2
              className={cn(
                "text-lg font-semibold leading-snug text-content",
                !open && "text-muted line-through",
              )}
            >
              {meetingTitle(row)}
            </h2>
            <p className="mt-0.5 text-sm text-muted">
              {longDay(row.startAt)} · {timeRange(row.startAt, row.endAt)}
              {minutes ? <span className="text-faint"> ({duration(minutes)})</span> : null}
            </p>
            <p className="mt-0.5 text-xs text-faint">
              {tone === "cancelled"
                ? `Cancelled${row.cancelledByName ? ` by ${row.cancelledByName}` : ""}`
                : TONE_LABELS[tone]}
            </p>
          </div>
        </div>

        {!open && mine ? (
          <button
            type="button"
            disabled={act.isPending}
            onClick={() => act.mutate(() => salesApi.restoreMeeting(row.id))}
            className="ml-[1.625rem] text-xs font-medium text-brand-600 hover:underline disabled:opacity-50 dark:text-brand-400"
          >
            Put it back in the diary
          </button>
        ) : null}

        {act.isError ? (
          <p className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-500/10 dark:text-red-300">
            {act.error instanceof ApiError ? act.error.message : "That did not work."}
          </p>
        ) : null}

        {/* ── Where ────────────────────────────────────────────────────── */}
        {row.mode === "online" ? (
          row.meetingUrl ? (
            <Fact icon="video">
              <a
                href={row.meetingUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-sm font-medium text-white hover:bg-brand-700"
              >
                {row.meetingUrl.includes("meet.google.com") ? "Join with Google Meet" : "Join online"}
              </a>
              <span className="mt-1 block truncate text-xs text-faint">
                {row.meetingUrl.replace(/^https?:\/\//, "")}
              </span>
            </Fact>
          ) : (
            <Fact icon="video">
              <span className="text-faint">Online — no link yet</span>
            </Fact>
          )
        ) : row.location ? (
          <Fact icon="pin">
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(row.location)}`}
              target="_blank"
              rel="noreferrer"
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {row.location}
            </a>
          </Fact>
        ) : null}

        {/* ── Which company, and who there ─────────────────────────────── */}
        <Fact icon="building">
          {row.companyId ? (
            <Link
              to={`/crm/companies/${row.companyId}`}
              className="text-brand-600 hover:underline dark:text-brand-400"
            >
              {row.crmCompanyName ?? row.companyName}
            </Link>
          ) : (
            <span>
              {row.companyName}{" "}
              <span className="text-faint">· not linked to a CRM company</span>
            </span>
          )}
          {row.contactName ? (
            <span className="block text-xs text-muted">
              Seeing {row.contactName}
              {row.contactPhone ? (
                <>
                  {" · "}
                  <a href={`tel:${row.contactPhone}`} className="text-brand-600 hover:underline dark:text-brand-400">
                    {row.contactPhone}
                  </a>
                </>
              ) : null}
            </span>
          ) : null}
        </Fact>

        {/* ── Guests ───────────────────────────────────────────────────── */}
        <Fact icon="person">
          <div className="flex items-baseline gap-2">
            <span className="font-medium">
              {going === 1 ? "1 guest" : `${going} guests`}
            </span>
            {editable ? (
              <button
                type="button"
                onClick={() => onOpen("guests")}
                className="ml-auto text-xs text-brand-600 hover:underline dark:text-brand-400"
              >
                Edit guests
              </button>
            ) : null}
          </div>
          <p className="text-xs text-faint">
            {row.attendees.length} from NetXL
            {guests !== null || row.externalGuestCount > 0
              ? `, ${guests?.length ?? row.externalGuestCount} invited`
              : ""}
          </p>
          <People meeting={row} guests={guests} user={user} loading={detail.isLoading} />
        </Fact>

        {/* ── Notes, which is what a past meeting is opened for ────────── */}
        <Fact icon="notes">
          {row.notesPreview ? (
            <button
              type="button"
              onClick={() => onOpen("notes")}
              className="block w-full text-left"
            >
              <span className="line-clamp-3 text-sm text-content">{row.notesPreview}</span>
              <span className="text-xs text-brand-600 hover:underline dark:text-brand-400">
                Open notes
              </span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onOpen("notes")}
              className={cn(
                "text-sm hover:underline",
                past && open
                  ? "font-medium text-amber-700 dark:text-amber-400"
                  : "text-brand-600 dark:text-brand-400",
              )}
            >
              {past && open ? "No notes yet — write them up" : "Add notes"}
            </button>
          )}
          {past && open ? (
            <span className="mt-0.5 block text-xs text-faint">
              {row.followUpCount > 0
                ? `Follow-up sent${row.followUpCount > 1 ? ` (${row.followUpCount})` : ""}`
                : "No follow-up sent yet"}
            </span>
          ) : null}
        </Fact>

        {detail.data?.details ? (
          <p className="ml-7 line-clamp-4 whitespace-pre-wrap text-xs text-muted">
            {detail.data.details}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Everybody going: ours first, then the people invited from outside. */
function People({
  meeting,
  guests,
  user,
  loading,
}: {
  meeting: SalesMeeting;
  guests: SalesMeetingDetail["guests"] | null;
  user: User;
  loading: boolean;
}) {
  const [all, setAll] = useState(false);
  const people = [
    ...meeting.attendees.map((person) => ({
      key: `u${person.userId}`,
      avatar: <Avatar agent={{ id: person.userId, name: person.name }} size="sm" />,
      name: person.userId === user.id ? `${person.name ?? "You"} (you)` : (person.name ?? "Somebody"),
      note:
        meeting.googleOrganiserUserId === person.userId ? "Organiser" : null,
    })),
    ...(guests ?? []).map((guest) => ({
      key: guest.email,
      avatar: (
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-2 text-xs font-medium text-muted-on-tint">
          {(guest.name ?? guest.email)[0]!.toUpperCase()}
        </span>
      ),
      name: guest.name ?? guest.email,
      note: guest.name ? guest.email : null,
    })),
  ];
  const shown = all ? people : people.slice(0, SHOWN);

  return (
    <ul className="mt-2 space-y-1.5">
      {shown.map((person) => (
        <li key={person.key} className="flex items-center gap-2.5">
          {person.avatar}
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-content">{person.name}</span>
            {person.note ? (
              <span className="block truncate text-[11px] text-faint">{person.note}</span>
            ) : null}
          </span>
        </li>
      ))}
      {people.length > shown.length ? (
        <li>
          <button
            type="button"
            onClick={() => setAll(true)}
            className="text-xs text-brand-600 hover:underline dark:text-brand-400"
          >
            and {people.length - shown.length} more
          </button>
        </li>
      ) : null}
      {loading && meeting.externalGuestCount > 0 ? (
        <li className="text-xs text-faint">Fetching guests…</li>
      ) : null}
    </ul>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-9 items-center justify-center rounded-full text-muted hover:bg-surface-2 hover:text-content disabled:opacity-50"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        {children}
      </svg>
    </button>
  );
}

/** The block's hue, as the square Google puts beside the title. */
const SWATCH = {
  visit: "bg-sky-600",
  call: "bg-violet-600",
  internal: "bg-emerald-600",
  cancelled: "bg-zinc-400",
} as const;
